import { Icon } from '@dixa/lib-shared-habla';
import { typography } from '@dixa/lib-shared-tokens';
import type { ConversationMessageDeliveryEventStatus } from 'lib-shared-types-api';
import type { TemplateExecutor } from 'lodash';
import template from 'lodash/template';
import { ReactNode } from 'react';

/**
 * Gets various attributes related to the conversation message delivery status.
 *
 * @param status The delivery status type.
 */
export function getConversationMessageDeliveryStatusAttributes(
  status: ConversationMessageDeliveryEventStatus,
): ConversationMessageDeliveryStatusAttributes {
  return (
    conversationMessageDeliveryStatusAttributes[status] ??
    conversationMessageDeliveryUnknownStatusAttributes
  );
}

/** Represents the conversation message delivery status type attributes. */
export const conversationMessageDeliveryStatusAttributes: Record<
  ConversationMessageDeliveryEventStatus,
  ConversationMessageDeliveryStatusAttributes
> = {
  info: {
    icon: <Icon color={typography.color.tertiary} name="info" />,
    labelTemplate: 'Info',
  },
  delivered: {
    icon: <Icon color={typography.color.tertiary} name="check-circle-filled" />,
    labelTemplate: template('Delivered${info}'),
  },
  failed: {
    icon: <Icon color={typography.color.error} name="close-circle-filled" />,
    labelTemplate: template('Delivery failed${info}'),
  },
  seen: {
    icon: (
      <Icon
        color={typography.color.tertiary}
        name="double-check-circle-filled"
      />
    ),
    labelTemplate: template('Read${info}'),
  },
  sent: {
    icon: <Icon color={typography.color.tertiary} name="check-circle" />,
    labelTemplate: template('Sent${info}'),
  },
  spam: {
    icon: (
      <Icon color={typography.color.warning} name="exclamation-circle-filled" />
    ),
    labelTemplate: template('Marked as spam${info}'),
  },
};

export const conversationMessageDeliveryUnknownStatusAttributes: ConversationMessageDeliveryStatusAttributes =
  {
    icon: <Icon color={typography.color.tertiary} name="question" />,
    labelTemplate: 'Unknown status',
  };

export type ConversationMessageDeliveryStatusAttributes = {
  /** The delivery status icon. */
  readonly icon: ReactNode;
  /**
   * The label template.
   *
   * Available variables:
   *
   * - `${info}`
   */
  readonly labelTemplate: TemplateExecutor | string;
};
